import { AdminApi, Document, DocumentsApi, Money } from 'api';
import useApiCall from 'hooks/use-api-call';
import React, { FunctionComponent, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalFooter, ModalContent, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import { FileWithDocType } from 'ui/atoms/file-upload';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';

export interface EditTaxInformationFields {
  id: string;
  taxIdentificationNumber: string;
  nonAssessmentCertificate: boolean;
  nonAssessmentCertificateDocId: string;
  nonAssessmentCertificateDoc: FileWithDocType;
  isSubjectToGermanTax: boolean;
}
export interface EditTaxInformationModalProps {
  onHideEditTaxInformation: () => void;
  fetchInvestorDetails: () => void;
  showSuccessModal: () => void;
  className?: string;
  exemptionOrderSum?: Money | null;
  values: {
    id: string;
    isSubjectToGermanTax?: boolean;
    taxIdentificationNumber: string;
    nonAssessmentCertificate: boolean;
    nonAssessmentCertificateDoc?: Document;
  };
}

const EditTaxInformationForm = makeModalForm<EditTaxInformationFields>();

const EditTaxInformationModal: FunctionComponent<EditTaxInformationModalProps> = (props) => {
  const { onHideEditTaxInformation, fetchInvestorDetails, showSuccessModal = () => {}, values } = props;

  const [uploadedDocument, setUploadedDocument] = useState<Document | undefined | null>(
    values?.nonAssessmentCertificateDoc || null,
  );

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const investorsApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  // useEffect(() => {
  //   const exemptionSum = investmentsData.reduce((previousSum, investment) => {
  //     if (!investment.exemptionOrderAmount) return previousSum;
  //     return previousSum + toNumber(investment.exemptionOrderAmount);
  //   }, 0);
  //   setExemptionOrderTotal(exemptionSum);
  // }, [investmentsData]);

  const editTaxInformation = (fieldsValues: StateValues<EditTaxInformationFields>) => {
    withApi(async () => {
      const patchBody = {
        id: values.id,
        patchedAdminInvestorUpdateRequest: {
          taxInformation: {
            ...fieldsValues,
            nonAssessmentCertificate: fieldsValues.nonAssessmentCertificate,
            isSubjectToGermanTax: !fieldsValues.isSubjectToGermanTax,
          },
        },
      };

      if (fieldsValues.nonAssessmentCertificateDoc?.file) {
        const nonAssessmentCertificateDocResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.nonAssessmentCertificateDoc?.file,
        });
        patchBody.patchedAdminInvestorUpdateRequest.taxInformation.nonAssessmentCertificateDocId =
          nonAssessmentCertificateDocResponse.id;
      }

      await investorsApi.adminInvestorsPartialUpdate(patchBody);

      //   await updateExemptionOrdersAmount();
      fetchInvestorDetails();
      showSuccessModal();
      onHideEditTaxInformation();
    });
  };

  // const updateExemptionOrdersAmount = async () => {
  //   const promises: any = [];

  //   investmentsData.forEach((investment) => {
  //     if (!investment.exemptionOrderAmount) return;

  //     promises.push(
  //       investorsApi.adminInvestmentsPartialUpdate({
  //         id: investment.id,
  //         patchedAdminInvestmentDetailsRequest: {
  //           exemptionOrderAmount: investment.exemptionOrderAmount,
  //         },
  //       }),
  //     );
  //   });

  //   return Promise.all(promises);
  // };

  // const handleExemptionChange = (investment: AdminInvestment, value: string) => {
  //   investment.exemptionOrderAmount = toMoney(value);
  //   setInvestmentsData([...investmentsData]);
  // };

  return (
    <>
      <Modal onClose={onHideEditTaxInformation}>
        <EditTaxInformationForm
          initial={{
            taxIdentificationNumber: values.taxIdentificationNumber,
            isSubjectToGermanTax: values.isSubjectToGermanTax === undefined ? undefined : !values.isSubjectToGermanTax,
            nonAssessmentCertificate: values.nonAssessmentCertificate,
          }}
          onSubmit={(values: StateValues<EditTaxInformationFields>) => {
            return editTaxInformation(values);
          }}
          i18nKey="dashboardIssuerInvestorDetails.editTaxInformationForm"
        >
          <EditTaxInformationForm.ValueProvider>
            {(values) => {
              return (
                <>
                  <ModalHeader>
                    <Translate name="dashboardIssuerInvestorDetails.editTaxInformationForm.title" />
                  </ModalHeader>
                  <ModalContent>
                    <strong>
                      <Translate as="p" name="dashboardIssuerInvestorDetails.editTaxInformationForm.subtitleTaxId" />
                    </strong>
                    <EditTaxInformationForm.Group name="taxIdentificationNumber" required={true}>
                      <EditTaxInformationForm.Input placeholder="-" />
                    </EditTaxInformationForm.Group>
                    <EditTaxInformationForm.Group name="isSubjectToGermanTax" label={null}>
                      <EditTaxInformationForm.Checkbox>
                        <Translate name="dashboardIssuerInvestorDetails.editTaxInformationForm.fields.isSubjectToGermanTax.label" />
                      </EditTaxInformationForm.Checkbox>
                    </EditTaxInformationForm.Group>
                    <strong>
                      <Translate
                        as="p"
                        name="dashboardIssuerInvestorDetails.editTaxInformationForm.subtitleNonAssessmentCertificate"
                      />
                    </strong>
                    <EditTaxInformationForm.Group name="nonAssessmentCertificate" label={null}>
                      <EditTaxInformationForm.Checkbox>
                        <Translate name="dashboardIssuerInvestorDetails.editTaxInformationForm.fields.hasNonAssessmentCertificate.label" />
                      </EditTaxInformationForm.Checkbox>
                    </EditTaxInformationForm.Group>
                    {!!values.nonAssessmentCertificate && (
                      <>
                        {uploadedDocument && (
                          <FileUploadDocumentContainer
                            uploadedFile={{
                              file: {
                                name: uploadedDocument.name,
                                size: uploadedDocument.sizeInBytes,
                              } as File,
                            }}
                            onRemoveDocument={() => setUploadedDocument(null)}
                          />
                        )}
                        {!uploadedDocument && (
                          <>
                            <Translate
                              as="p"
                              name="dashboardIssuerInvestorDetails.editTaxInformationForm.uploadCertificate"
                            />
                            <EditTaxInformationForm.Group
                              name="nonAssessmentCertificateDoc"
                              required={values.nonAssessmentCertificate}
                            >
                              <EditTaxInformationForm.FileUpload />
                            </EditTaxInformationForm.Group>
                          </>
                        )}
                      </>
                    )}

                    {/* {investmentsData.length > 0 && (
                      <Grid>
                        <Col width={8}>
                          <strong>
                            <Translate
                              name={`dashboardIssuerInvestorDetails.editTaxInformationForm.fields.exemptionOrder.orders`}
                            />
                          </strong>
                        </Col>
                        <Col width={4}>
                          <strong>
                            <Translate
                              name={`dashboardIssuerInvestorDetails.editTaxInformationForm.fields.exemptionOrder.lastlyChanged`}
                            />
                          </strong>
                        </Col>
                      </Grid>
                    )}

                    {investmentsData.length > 0 &&
                      investmentsData.map((investment, key) => {
                        return (
                          <Grid key={key}>
                            <Col width={8}>
                              <strong>
                                <small>{investment.productName}</small>
                              </strong>
                              <Input
                                type="number"
                                min={0}
                                onChange={(e: any) => {
                                  handleExemptionChange(investment, e.target.value);
                                }}
                                value={
                                  investment.exemptionOrderAmount ? toNumber(investment.exemptionOrderAmount).toString() : '0'
                                }
                                isCurrency
                              />
                              {EditTaxInformationForm.Validators.Range(0, undefined)}
                              {EditTaxInformationForm.Validators.MaxDecimals(2)}
                            </Col>
                            <Col width={4}>
                              {investment.exemptionOrderDate && (
                                <Styled.DateContainer>
                                  <Icon name="history" size="regular" color="gray" />
                                  <small>
                                    <Date>{investment.exemptionOrderDate}</Date>
                                  </small>
                                </Styled.DateContainer>
                              )}
                            </Col>
                          </Grid>
                        );
                      })} */}

                    {/* {exemptionOrderSum && (
                      <Container>
                        <Grid>
                          <small>
                            <Translate
                              name={`dashboardIssuerInvestorDetails.editTaxInformationForm.fields.exemptionOrder.total`}
                            />
                            <strong>
                              <Currency>{toMoney(exemptionOrderTotal)}</Currency>
                            </strong>
                          </small>
                        </Grid>
                      </Container>
                    )} */}
                  </ModalContent>
                  <ModalFooter>
                    <ModalFooterButtons
                      actionButtons={[
                        {
                          name: 'cancel',
                          content: <Translate name="common.cancel" />,
                          size: 'large',
                          onClick: onHideEditTaxInformation,
                        },
                        {
                          name: 'update',
                          content: <Translate name="common.saveChanges" />,
                          variant: 'primary',
                          type: 'submit',
                          size: 'large',
                        },
                      ]}
                    />
                  </ModalFooter>
                </>
              );
            }}
          </EditTaxInformationForm.ValueProvider>
        </EditTaxInformationForm>
      </Modal>
    </>
  );
};

export default EditTaxInformationModal;
